import { Component, Input, Output, EventEmitter, computed } from '@angular/core';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * Themed action button with variant, size, loading state, and disabled handling built in.
 */
@Component({
  selector: 'es-button',
  template: `
    <button
      [type]="type"
      [disabled]="disabled || loading"
      [class]="classes()"
      (click)="handleClick($event)"
    >
      @if (loading) {
        <span class="animate-spin">◌</span>
      }
      <ng-content></ng-content>
    </button>
  `,
})
export class ButtonComponent {
  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'md';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() fullWidth = false;

  @Output() clicked = new EventEmitter<MouseEvent>();

  classes = computed(() => {
    const base = 'inline-flex items-center justify-center gap-2 rounded-lg font-semibold font-exo transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';
    const variants: Record<ButtonVariant, string> = {
      primary: 'bg-echo-blue text-black hover:bg-echo-blue-300',
      secondary: 'bg-transparent border border-[var(--border)] text-[var(--text-secondary)] hover:border-echo-blue hover:text-echo-blue',
      danger: 'bg-transparent border border-red-500/30 text-red-500 hover:bg-red-500/10',
      ghost: 'bg-transparent text-[var(--text-secondary)] hover:text-[var(--text)] hover:bg-[var(--bg-card-hover)]',
    };
    const sizes: Record<ButtonSize, string> = {
      sm: 'px-3 py-1.5 text-xs h-8',
      md: 'px-5 py-2 text-sm h-9',
      lg: 'px-6 py-2.5 text-base h-11',
    };
    const width = this.fullWidth ? 'w-full' : '';
    return `${base} ${variants[this.variant]} ${sizes[this.size]} ${width}`;
  });

  handleClick(event: MouseEvent) {
    if (!this.disabled && !this.loading) {
      this.clicked.emit(event);
    }
  }
}
